// i18next setup for the operator dashboard.
//
// English is bundled synchronously: it is the fallback for every other locale,
// and bundling it keeps the first paint (and the test suite, which asserts on
// English copy) free of an async hop.
//
// Every other locale is one lazily imported JSON file holding all namespaces.
// Namespaces are real i18next namespaces, so `useTranslation("nav")` call
// sites are already final — splitting the *transport* per namespace later is a
// bundling change with no effect on callers.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";
import { DEFAULT_INTERFACE_LANGUAGE, INTERFACE_LANGUAGE_CODES } from "../utils/interfaceLanguages";

export const I18N_NAMESPACES = ["common", "settings", "nav"] as const;
export type I18nNamespace = (typeof I18N_NAMESPACES)[number];

export const DEFAULT_NAMESPACE: I18nNamespace = "common";

type LocaleBundle = Record<string, Record<string, unknown>>;

/**
 * Static import per locale so the bundler can see and split them. Keyed by the
 * registry's codes; `en` is absent because it ships eagerly above.
 */
const LOCALE_BUNDLES: Record<string, () => Promise<{ default: LocaleBundle }>> = {
    es: () => import("./locales/es.json"),
    fr: () => import("./locales/fr.json"),
    de: () => import("./locales/de.json"),
    "pt-BR": () => import("./locales/pt-BR.json"),
    it: () => import("./locales/it.json"),
    hi: () => import("./locales/hi.json"),
    "zh-Hans": () => import("./locales/zh-Hans.json"),
    ja: () => import("./locales/ja.json"),
    ru: () => import("./locales/ru.json"),
};

const addBundle = (code: string, bundle: LocaleBundle) => {
    for (const ns of I18N_NAMESPACES) {
        if (bundle[ns]) i18n.addResourceBundle(code, ns, bundle[ns], true, true);
    }
};

const inFlight = new Map<string, Promise<void>>();

/**
 * Pull a locale's catalogue into the running i18next instance, once. Failure
 * resolves rather than throws: an unreachable chunk should leave the operator
 * reading English, not staring at an error boundary.
 */
export function loadLocale(code: string): Promise<void> {
    if (code === DEFAULT_INTERFACE_LANGUAGE || i18n.hasResourceBundle(code, DEFAULT_NAMESPACE)) {
        return Promise.resolve();
    }

    const existing = inFlight.get(code);
    if (existing) return existing;

    const loader = LOCALE_BUNDLES[code];
    if (!loader) return Promise.resolve();

    const pending = loader()
        .then((mod) => addBundle(code, mod.default))
        .catch((error) => {
            console.error(`[i18n] failed to load catalogue for "${code}"`, error);
        });

    inFlight.set(code, pending);
    return pending;
}

/**
 * A key missing from the *English* catalogue is a typo or an un-extracted
 * string — a bug either way. Missing from a translation is the expected state
 * of a catalogue still being filled, and must stay silent.
 */
const reportMissingKey = (languages: readonly string[], namespace: string, key: string) => {
    if (!languages.includes(DEFAULT_INTERFACE_LANGUAGE)) return;
    const message = `[i18n] missing key "${namespace}:${key}"`;
    if (process.env.NODE_ENV === "test") throw new Error(message);
    if (process.env.NODE_ENV !== "production") console.error(message);
};

i18n.use(initReactI18next).init({
    resources: { en },
    lng: DEFAULT_INTERFACE_LANGUAGE,
    fallbackLng: DEFAULT_INTERFACE_LANGUAGE,
    supportedLngs: [...INTERFACE_LANGUAGE_CODES],
    ns: [...I18N_NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    interpolation: { escapeValue: false },
    // Catalogues are loaded explicitly by `LocaleProvider`, so there is never a
    // backend to suspend on.
    react: { useSuspense: false },
    returnNull: false,
    saveMissing: process.env.NODE_ENV !== "production",
    missingKeyHandler: (lngs, ns, key) => reportMissingKey(lngs ?? [], ns, key),
});

export default i18n;
